import React, { useEffect, useState } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { toast } from '../utils/toastBus';

export default function UploadsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [serverTotal, setServerTotal] = useState(null);
  const [sort, setSort] = useState('modified:desc');

  function formatBytes(bytes) {
    const n = typeof bytes === 'number' ? bytes : Number(bytes);
    if (!n || n < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(n) / Math.log(1024));
    const v = (n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1);
    return `${v} ${units[i]}`;
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      if (!query) {
        const res = await api.listUploads({ page, limit: pageSize, sort });
        if (res?.error) throw new Error(res.error);
        const arr = Array.isArray(res?.items) ? res.items : [];
        setItems(arr);
        setServerTotal(typeof res?.total === 'number' ? res.total : null);
      } else {
        const res = await api.listUploads({ page, limit: pageSize, search: query, sort });
        if (res?.error) throw new Error(res.error);
        const arr = Array.isArray(res?.items) ? res.items : [];
        setItems(arr);
        setServerTotal(typeof res?.total === 'number' ? res.total : null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [page, pageSize, query, sort]);

  // Derived: filtered + paginated list
  const filtered = items.filter(f => !query || f.name.toLowerCase().includes(query.toLowerCase()))
  const usingServer = typeof serverTotal === 'number';
  const total = usingServer ? serverTotal : filtered.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, maxPage);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageItems = usingServer ? items : filtered.slice(start, end);

  // Ensure links to /uploads resolve against backend origin in dev to avoid SPA intercepts
  function resolveMediaUrl(u) {
    if (!u || typeof u !== 'string') return '';
    if (u.startsWith('/uploads/')) return `${API_BASE}${u}`;
    return u;
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: '#ff7a45' }}>Uploads (Admin)</h2>
      <p style={{ color: '#9ca3af' }}>Lists files served from /uploads. Endpoint requires admin role.</p>
      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p style={{ color: '#fca5a5' }}>{error}</p>
      ) : (
        <>
        <div style={{ margin: '8px 0 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input id="uploads-filter" name="q" placeholder="Filter by name…" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }} />
          <select id="uploads-sort" name="sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} style={{ padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
            <option value="modified:desc">Newest</option>
            <option value="modified:asc">Oldest</option>
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
            <option value="size:desc">Size High→Low</option>
            <option value="size:asc">Size Low→High</option>
          </select>
          <select id="uploads-page-size" name="pageSize" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Name</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Size</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Modified</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((f) => (
              <tr key={f.name}>
                <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{f.name}</td>
                <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{formatBytes(f.size)}</td>
                <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{f.modified}</td>
                <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>
                  {(() => {
                    const href = resolveMediaUrl(f.url);
                    return (
                      <>
                        <a href={href} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', marginRight: 12 }}>Open</a>
                        <a href={href} download style={{ color: '#34d399' }}>Download</a>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete ${f.name}? This cannot be undone.`)) return;
                          try {
                            const r = await api.deleteUploadAdmin(f.name);
                            if (r?.ok !== true) throw new Error(r?.error || 'Delete failed');
                            toast({ type: 'success', message: 'Deleted' });
                            await load();
                          } catch (e) {
                            toast({ type: 'error', message: e?.message || 'Delete failed' });
                          }
                        }} style={{ marginLeft: 12, background: '#ef4444', color: '#fff', border: 0, borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>Delete</button>
                      </>
                    );
                  })()}
                </td>
              </tr>
            ))}
            {total === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: '10px 4px', color: '#9ca3af' }}>No files</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <span style={{ color: '#9ca3af' }}>
            Showing {total === 0 ? 0 : start + 1}-{Math.min(end, total)} of {total}
            {usingServer && ' (server)'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ padding: '6px 10px', background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6 }}>Prev</button>
            <span style={{ color: '#9ca3af' }}>Page {safePage}/{maxPage}</span>
            <button disabled={safePage >= maxPage} onClick={() => setPage(p => Math.min(maxPage, p + 1))} style={{ padding: '6px 10px', background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6 }}>Next</button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
